import React from 'react'
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import { Link, LinkOff } from '@material-ui/icons'
import { actionsByStatus, experienceActionsByRole, userRoles } from 'config/constants'
import { generateRandomKey } from 'utils'
import { useAuth } from 'context'
import ActionIcon from './ActionIcon'

const styles = makeStyles(theme => ({
  toolsContainer: {
    borderRadius: 20,
    background: theme.palette.gray[1],
    padding: theme.spacing(0.5, 1.3),
  },
  auto: {
    width: `auto`,
  },
  normalWidth: {
    width: 150,
  },
  tooltip: {
    background: `rgba(0, 0, 0, 0.7)`,
    padding: theme.spacing(0.5, 1),
    borderRadius: 5,
    color: theme.palette.primary.light,
  },
  linkIconContainer: {
    padding: theme.spacing(1),
    borderRadius: `50%`,
    display: `flex`,
    alignItems: `center`,
    transition: `0.3s`,
    '&:hover': {
      opacity: 0.5,
    },
  },
  linkIcon: {
    fill: theme.palette.primary.light,
  },
  availableLink: {
    background: `linear-gradient(180deg, #007BFF 0%, #0064BA 100%)`,
  },
  noAvailableLink: {
    background: `linear-gradient(0deg, #C8270C 0%, #F45438 100%)`,
  },
  noVerifyLink: {
    background: `linear-gradient(0deg,#606061 0%, #89898A 100%)`,
  },
  watchContainer: {
    width: 150,
  },
  watchLabel: {
    padding: theme.spacing(0, 1),
    textDecoration: `underline solid ${theme.palette.success.main}`,
    color: theme.palette.success.main,
    fontSize: theme.fontSize[0],
    fontWeight: theme.fontWeight[0],
    cursor: `pointer`,
  },
}))

export default function ExperienceToolsBar({
  role,
  experiences_status_id: status,
  id,
  ExtraInformation = null,
  onChange = () => {},
  autoWitdth = false,
  isNationalValidator = false,
}) {
  const classes = styles()
  const availableExpActions = actionsByStatus[status].filter(action =>
    experienceActionsByRole[role].includes(action),
  )

  const {
    user: { permisos = [], Region = [] },
  } = useAuth()

  const validateByActions = (validate, action) => {
    if (availableExpActions.includes(action) && action === `validate` && status === 10) {
      return isNationalValidator
    }
    if (availableExpActions.includes(action) && validate === `Validatorderivar`) {
      return permisos.includes(`derivarRegiones`) || Region.includes(7)
    }
    return availableExpActions.includes(action)
  }

  const dataIcon = data =>
    ({
      availableLink: {
        text: `Direcciones web o URL incluidos son correctos`,
        iconLink: <Link className={classes.linkIcon} />,
      },
      noAvailableLink: {
        text: `Direcciones web o URL incluidos son incorrectos o erróneos`,
        iconLink: <LinkOff className={classes.linkIcon} />,
      },
      noVerifyLink: {
        text: `Las URL de esta experiencia serán validadas después de 24 horas`,
        iconLink: <LinkOff className={classes.linkIcon} />,
      },
    }[data] || `noAvailableLink`)

  function getLinkIndicatorIcon() {
    if (ExtraInformation) {
      const {
        website_url: websiteUrl = null,
        website_url_valid: websiteUrlValid = false,
        shoppingcart_url: shoppingcartUrl = null,
        shoppingcart_url_valid: shoppingcartUrlValid = false,
        tripadvisor = null,
        tripadvisor_valid_url: tripadvisorValidUrl = false,
        date_validation_cron: dateValidationCron = null,
      } = ExtraInformation
      let icon = null
      if (!websiteUrl && !shoppingcartUrl && !tripadvisor) {
        return null
      }

      if (websiteUrl) {
        icon = websiteUrlValid ? `availableLink` : `noAvailableLink`
      }
      if (shoppingcartUrl) {
        icon = shoppingcartUrlValid ? `availableLink` : `noAvailableLink`
      }
      if (tripadvisor) {
        icon = tripadvisorValidUrl ? `availableLink` : `noAvailableLink`
      }

      if (!dateValidationCron) {
        icon = `noVerifyLink`
      }

      if (icon) {
        if (icon === `availableLink` && role === userRoles.Admin) {
          return null
        }
        const { text = null, iconLink = null } = dataIcon(icon)
        return (
          <Tooltip
            title={
              <Typography
                className={classes.tooltip}
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
              />
            }
            placement="top"
          >
            <Grid className={`${classes.linkIconContainer} ${classes[icon]}`}>{iconLink}</Grid>
          </Tooltip>
        )
      }
      return null
    }
    return null
  }

  function getIconsToRender() {
    let iconsToRend = experienceActionsByRole[role].map(action => ({
      action,
      active: validateByActions(`${role}${action}`, action),
    }))
    if (
      experienceActionsByRole[role].includes(`play`) ||
      experienceActionsByRole[role].includes(`pause`)
    ) {
      const actionsToDelete = availableExpActions.includes(`play`)
        ? [`watch`, `pause`]
        : [`watch`, `play`]
      iconsToRend = iconsToRend.filter(({ action }) => !actionsToDelete.includes(action))
    }
    if (
      experienceActionsByRole[role].includes(`desactivate`) ||
      experienceActionsByRole[role].includes(`activate`)
    ) {
      const actionsToDelete = availableExpActions.includes(`desactivate`)
        ? [`activate`]
        : [`desactivate`]
      iconsToRend = iconsToRend.filter(({ action }) => !actionsToDelete.includes(action))
    }
    if (
      experienceActionsByRole[role].includes(`delete`) ||
      experienceActionsByRole[role].includes(`deleteUpgrade`)
    ) {
      const actionsToDelete = availableExpActions.includes(`delete`)
        ? [`deleteUpgrade`]
        : [`delete`]
      iconsToRend = iconsToRend.filter(({ action }) => !actionsToDelete.includes(action))
    }
    return iconsToRend
  }

  function renderInfoLink() {
    return (
      <Grid container justify="center" alignItems="center" className={classes.watchContainer}>
        <ActionIcon action="watch" active onChange={onChange} id={id} />
        <Typography
          variant="body1"
          className={classes.watchLabel}
          onClick={() => onChange({ action: `watch`, experienceId: id })}
        >
          Ver Información
        </Typography>
        <ActionIcon action="delete" active onChange={onChange} id={id} />
      </Grid>
    )
  }

  function renderToolBar() {
    return (
      <Grid
        container
        justify="space-between"
        alignItems="center"
        className={`${classes.toolsContainer} ${autoWitdth ? classes.auto : classes.normalWidth}`}
      >
        {getIconsToRender().map(props => (
          <ActionIcon
            {...props}
            {...ExtraInformation}
            key={generateRandomKey(`experience-action`)}
            onChange={onChange}
            id={id}
          />
        ))}
      </Grid>
    )
  }

  return (
    <Grid container justify="space-between" alignItems="center">
      {availableExpActions.includes(`watch`) ? renderInfoLink() : renderToolBar()}
      {getLinkIndicatorIcon()}
    </Grid>
  )
}
